import { CheckCircle, Clock } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  generateCheckoutSession,
  getCustomerPortalUrl,
  getMyB2bGrantInfo,
  useQuery,
} from "wasp/client/operations";
import { Alert, AlertDescription } from "../client/components/ui/alert";
import { Button } from "../client/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardTitle,
} from "../client/components/ui/card";
import { cn } from "../client/utils";
import {
  PaymentPlanId,
  paymentPlans,
  prettyPaymentPlanName,
  SubscriptionStatus,
} from "./plans";
import { trackUpgradeStarted } from "../client/utils/posthog";
import useTranslation from "../client/hooks/useTranslation";
import { IS_BETA_MODE } from "../client/betaConfig";

const bestDealPaymentPlanId: PaymentPlanId = PaymentPlanId.Max;

// A feature line is either a plain label or a label with a status pill
// ("soon" = not shipped yet, "beta" = live but in beta).
type PlanFeature = string | { label: string; badge?: "soon" | "beta" };

interface PaymentPlanCard {
  name: string;
  price: string;
  description: string;
  features: PlanFeature[];
  badge?: string;
  savings?: string;
  savingsPercent?: string;
}

// This will be populated with translations in the component
export const getPaymentPlanCards = (t: any): Record<PaymentPlanId, PaymentPlanCard> => ({
  [PaymentPlanId.Free]: {
    name: prettyPaymentPlanName(PaymentPlanId.Free),
    price: t.pricing.free.price,
    description: t.pricing.free.description,
    features: [
      t.pricing.free.features.trades,
      t.pricing.free.features.accounts,
      t.pricing.free.features.sessions,
      t.pricing.free.features.routines,
      t.pricing.free.features.goals,
      t.pricing.free.features.analytics,
      t.pricing.free.features.support,
    ],
  },
  [PaymentPlanId.Pro]: {
    name: prettyPaymentPlanName(PaymentPlanId.Pro),
    price: "€15",
    description: t.pricing.pro.description,
    badge: t.pricing.pro.badge,
    features: [
      t.pricing.pro.features.trades,
      t.pricing.pro.features.accounts,
      t.pricing.pro.features.propFirm,
      t.pricing.pro.features.customPages,
      t.pricing.pro.features.mentorMode,
      t.pricing.pro.features.sessions,
      t.pricing.pro.features.analytics,
      t.pricing.pro.features.routines,
      t.pricing.pro.features.goals,
      t.pricing.pro.features.support,
      t.pricing.pro.features.futureFeatures,
    ],
  },
  [PaymentPlanId.Max]: {
    name: prettyPaymentPlanName(PaymentPlanId.Max),
    price: "€29",
    description: t.pricing.max.description,
    features: [
      t.pricing.max.features.allPro,
      { label: t.pricing.max.features.tradovate, badge: "beta" },
      { label: t.pricing.max.features.metaApi, badge: "soon" },
      t.pricing.max.features.support,
      t.pricing.max.features.futureFeatures,
    ],
  },
  [PaymentPlanId.ProAnnual]: {
    name: prettyPaymentPlanName(PaymentPlanId.Pro),
    price: t.pricing.pro.priceAnnual,
    description: t.pricing.pro.description,
    badge: t.pricing.pro.badge,
    savings: t.pricing.pro.totalAnnual,
    savingsPercent: t.pricing.pro.savingsPercent,
    features: [
      t.pricing.pro.features.trades,
      t.pricing.pro.features.accounts,
      t.pricing.pro.features.propFirm,
      t.pricing.pro.features.customPages,
      t.pricing.pro.features.mentorMode,
      t.pricing.pro.features.sessions,
      t.pricing.pro.features.analytics,
      t.pricing.pro.features.routines,
      t.pricing.pro.features.goals,
      t.pricing.pro.features.support,
      t.pricing.pro.features.futureFeatures,
    ],
  },
  [PaymentPlanId.MaxAnnual]: {
    name: prettyPaymentPlanName(PaymentPlanId.Max),
    price: t.pricing.max.priceAnnual,
    description: t.pricing.max.description,
    savings: t.pricing.max.totalAnnual,
    savingsPercent: t.pricing.max.savingsPercent,
    features: [
      t.pricing.max.features.allPro,
      { label: t.pricing.max.features.tradovate, badge: "beta" },
      { label: t.pricing.max.features.metaApi, badge: "soon" },
      t.pricing.max.features.support,
      t.pricing.max.features.futureFeatures,
    ],
  },
});

const PricingPage = () => {
  const t = useTranslation();
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');

  const { data: user } = useAuth();
  const paymentPlanCards = getPaymentPlanCards(t);
  const isUserSubscribed =
    !!user &&
    !!user.subscriptionStatus &&
    user.subscriptionStatus !== SubscriptionStatus.Deleted;
  // Membre B2B : Max offert par un partenaire, rien à acheter ni à gérer ici.
  // Page visible déconnecté : tout accès à user reste optionnel.
  const isB2bMember = !!user?.b2bGrantId;
  const { data: b2bGrantInfo } = useQuery(getMyB2bGrantInfo, undefined, {
    enabled: isB2bMember,
  });

  const {
    data: customerPortalUrl,
    isLoading: isCustomerPortalUrlLoading,
    error: customerPortalUrlError,
  } = useQuery(getCustomerPortalUrl, { enabled: isUserSubscribed });

  const navigate = useNavigate();

  function resolvedPlanId(planId: PaymentPlanId): PaymentPlanId {
    if (billingPeriod === 'annual') {
      if (planId === PaymentPlanId.Pro) return PaymentPlanId.ProAnnual;
      if (planId === PaymentPlanId.Max) return PaymentPlanId.MaxAnnual;
    }
    return planId;
  }

  async function handleBuyNowClick(paymentPlanId: PaymentPlanId) {
    if (isB2bMember) return;
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      setIsPaymentLoading(true);

      const resolved = resolvedPlanId(paymentPlanId);
      const checkoutResults = await generateCheckoutSession(resolved);

      if (resolved === PaymentPlanId.ProAnnual) {
        trackUpgradeStarted("pro_annual");
      } else if (resolved === PaymentPlanId.MaxAnnual) {
        trackUpgradeStarted("max_annual");
      } else if (resolved === PaymentPlanId.Pro) {
        trackUpgradeStarted("pro");
      } else if (resolved === PaymentPlanId.Max) {
        trackUpgradeStarted("max");
      }

      if (checkoutResults?.sessionUrl) {
        window.open(checkoutResults.sessionUrl, "_self");
      } else {
        throw new Error("Error generating checkout session URL");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Error processing payment. Please try again later.");
      }
      setIsPaymentLoading(false);
    }
  }

  const handleCustomerPortalClick = () => {
    if (isB2bMember) return;
    if (!user) {
      navigate("/login");
      return;
    }

    if (customerPortalUrlError) {
      setErrorMessage("Error fetching Customer Portal URL");
      return;
    }

    if (!customerPortalUrl) {
      setErrorMessage(`Customer Portal does not exist for user ${user.id}`);
      return;
    }

    window.open(customerPortalUrl, "_blank");
  };

  return (
    <div className="py-10 lg:mt-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {IS_BETA_MODE && (
          <div className="relative w-screen left-1/2 -translate-x-1/2 bg-primary text-white text-center py-3 px-4 text-sm font-semibold mb-4">
            {t.pricing.beta.banner}
          </div>
        )}
        {isB2bMember && (
          <Card className="mt-8 border-primary/30">
            <CardContent className="p-4">
              <CardTitle className="text-foreground text-sm font-semibold leading-5">
                {b2bGrantInfo
                  ? t.b2bMember.pricingBannerTitle.replace("{partner}", b2bGrantInfo.partnerName)
                  : t.b2bMember.checkoutBlocked}
              </CardTitle>
              {b2bGrantInfo && (
                <p className="text-muted-foreground mt-1 text-xs">
                  {t.b2bMember.pricingBannerBody}
                </p>
              )}
            </CardContent>
          </Card>
        )}
        {errorMessage && (
          <Alert variant="destructive" className="mt-8">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        {/* Billing period toggle */}
        <div className="flex items-center justify-center mt-8 mb-4">
          <div className="flex items-center gap-1 rounded-full border border-border bg-card p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200",
                billingPeriod === 'monthly'
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.pricing.monthly}
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={cn(
                "flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200",
                billingPeriod === 'annual'
                  ? "bg-background shadow text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.pricing.annual}
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                {t.pricing.savePercent}
              </span>
            </button>
          </div>
        </div>
        <div id="pricing" className="mt-16 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:mt-20">
          {[PaymentPlanId.Free, PaymentPlanId.Pro, PaymentPlanId.Max].map((planId) => {
            const displayPlanId = planId === PaymentPlanId.Free ? planId : resolvedPlanId(planId);
            const card = paymentPlanCards[displayPlanId];
            return (
            <Card
              key={planId}
              className={cn(
                "relative flex w-full flex-col justify-between overflow-hidden transition-all duration-300 hover:shadow-lg",
                {
                  "ring-primary !bg-transparent ring-2":
                    planId === bestDealPaymentPlanId,
                  "ring-border ring-1":
                    planId !== bestDealPaymentPlanId,
                },
              )}
            >
              {planId === bestDealPaymentPlanId && (
                <div
                  className="absolute right-0 top-0 -z-10 h-full w-full transform-gpu blur-3xl"
                  aria-hidden="true"
                >
                  <div
                    className="from-primary/40 via-primary/20 to-primary/10 absolute h-full w-full bg-gradient-to-br opacity-30"
                    style={{
                      clipPath: "circle(670% at 50% 50%)",
                    }}
                  />
                </div>
              )}
              <CardContent className="h-full justify-between p-3">
                <div className="flex items-center justify-between gap-x-2">
                  <CardTitle
                    id={planId}
                    className="text-foreground text-sm font-semibold leading-5"
                  >
                    {card.name}
                  </CardTitle>
                  {card.badge && (
                    <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap">
                      {card.badge}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-[10px] leading-3.5 line-clamp-2">
                  {card.description}
                </p>
                <p className="mt-2 flex items-baseline gap-x-1">
                  <span className="text-foreground text-xl font-bold tracking-tight">
                    {card.price}
                  </span>
                  {planId === PaymentPlanId.Pro && (
                    <span className="text-muted-foreground text-[9px] font-semibold leading-3">
                      {t.pricing.perMonth}
                    </span>
                  )}
                  {planId === PaymentPlanId.Max && (
                    <span className="text-muted-foreground text-[9px] font-semibold leading-3">
                      {t.pricing.perMonth}
                    </span>
                  )}
                </p>
                {billingPeriod === 'annual' && (card.savingsPercent || card.savings) && (
                  <p className="text-[10px] mt-0.5">
                    {card.savingsPercent && (
                      <span className="text-primary font-semibold">{card.savingsPercent}</span>
                    )}
                    {card.savingsPercent && card.savings && (
                      <span className="text-muted-foreground"> · </span>
                    )}
                    {card.savings && (
                      <span className="text-muted-foreground font-medium">{card.savings}</span>
                    )}
                  </p>
                )}
                <ul
                  role="list"
                  className="text-muted-foreground mt-2 space-y-1 text-[10px] leading-3.5"
                >
                  {card.features.map((feature) => {
                    const label = typeof feature === "string" ? feature : feature.label;
                    const badge = typeof feature === "string" ? undefined : feature.badge;
                    return (
                      <li key={label} className="flex items-start gap-x-1">
                        {badge === "soon" ? (
                          <Clock
                            className="h-3 w-3 flex-none mt-0.5 shrink-0 text-muted-foreground/60"
                            aria-hidden="true"
                          />
                        ) : (
                          <CheckCircle
                            className="text-primary h-3 w-3 flex-none mt-0.5 shrink-0"
                            aria-hidden="true"
                          />
                        )}
                        <span className={cn("flex-1 flex items-center gap-1 min-w-0", badge === "soon" && "text-muted-foreground/60")}>
                          <span className="line-clamp-1">{label}</span>
                          {badge === "soon" && (
                            <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-1 py-0 text-[8px] font-semibold uppercase leading-tight tracking-wide text-primary">
                              {t.common.soon}
                            </span>
                          )}
                          {badge === "beta" && (
                            <span className="inline-flex items-center rounded-full border border-warning/40 bg-warning/10 px-1 py-0 text-[8px] font-semibold uppercase leading-tight tracking-wide text-warning">
                              {t.common.beta}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
              <CardFooter className="p-3 pt-0">
                {isB2bMember ? (
                  planId === PaymentPlanId.Max ? (
                    <span className="flex h-8 w-full items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
                      {t.b2bMember.currentPlan}
                    </span>
                  ) : null
                ) : isUserSubscribed ? (
                  <Button
                    onClick={handleCustomerPortalClick}
                    disabled={isCustomerPortalUrlLoading}
                    aria-describedby="manage-subscription"
                    variant={
                      planId === bestDealPaymentPlanId ? "default" : "outline"
                    }
                    className="w-full text-xs py-1.5 h-8"
                    size="sm"
                  >
                    {t.pricing.manageSubscription}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (IS_BETA_MODE && planId !== PaymentPlanId.Free) {
                        return;
                      }
                      if (planId === PaymentPlanId.Free) {
                        navigate("/signup");
                      } else {
                        handleBuyNowClick(planId);
                      }
                    }}
                    aria-describedby={planId}
                    variant={planId === bestDealPaymentPlanId ? "default" : "outline"}
                    className="w-full text-xs py-1.5 h-8"
                    size="sm"
                    disabled={isPaymentLoading || (IS_BETA_MODE && planId !== PaymentPlanId.Free)}
                    title={IS_BETA_MODE && planId !== PaymentPlanId.Free ? t.pricing.beta.comingSoonTooltip : undefined}
                  >
                    {IS_BETA_MODE && planId !== PaymentPlanId.Free
                      ? t.pricing.beta.comingSoon
                      : planId === PaymentPlanId.Free
                      ? t.pricing.cta.free
                      : !!user
                      ? t.pricing.cta.buy
                      : t.pricing.cta.login}
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
